import { ArrowLeft, Search } from 'lucide-react'
import { useEffect, useRef, useState, type FormEvent, type RefObject } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { IconButton } from '@/components/ui'
import { cn } from '@/components/ui/cn'
import type { CommunityTabKey } from '@/lib/community'
import { t } from '@/lib/i18n'
import { searchContext, searchHref } from './shell-utils'

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}

export function SearchBox({ slug, tab }: { slug: string | null; tab: CommunityTabKey | null }) {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const inputRef = useRef<HTMLInputElement>(null)
  const mobileRef = useRef<HTMLInputElement>(null)
  const currentQ = params.get('q') ?? ''
  const [value, setValue] = useState(currentQ)
  const [mobileOpen, setMobileOpen] = useState(false)
  const { placeholder, type } = searchContext(slug, tab)

  // Keep the box in sync with the results page query (adjust state while rendering, no effect).
  const [syncedQ, setSyncedQ] = useState(currentQ)
  if (syncedQ !== currentQ) {
    setSyncedQ(currentQ)
    setValue(currentQ)
  }

  // "/" anywhere focuses search (SHELL-014 keeps the other shortcuts).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return
      e.preventDefault()
      const desktop = inputRef.current
      if (desktop && desktop.offsetParent !== null) desktop.focus()
      else setMobileOpen(true)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const q = value.trim()
    if (!q) return
    setMobileOpen(false)
    inputRef.current?.blur()
    const onResults = slug != null && window.location.pathname === `/${slug}/search`
    if (onResults) {
      // Searching again from the results page keeps the current tab and filters (SRCH-004).
      const next = new URLSearchParams(params)
      next.set('q', q)
      next.delete('p')
      navigate(`/${slug}/search?${next}`)
    } else {
      navigate(searchHref(slug, q, type))
    }
  }

  const field = (ref: RefObject<HTMLInputElement | null>, mobile: boolean) => (
    <div className="relative w-full">
      <Search
        size={18}
        strokeWidth={1.75}
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-subtle"
        aria-hidden="true"
      />
      <input
        ref={ref}
        autoFocus={mobile}
        type="search"
        enterKeyHint="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.currentTarget.blur()
            setMobileOpen(false)
          }
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        className={cn(
          'peer h-10 w-full rounded-control border border-transparent bg-search pl-10 text-body text-text outline-none',
          'transition-[background-color,box-shadow,border-color] duration-150 ease-out placeholder:text-subtle',
          'focus:border-focus focus:bg-surface focus:shadow-[0_0_0_3px_var(--color-focus-halo)]',
          '[&::-webkit-search-cancel-button]:hidden',
          mobile ? 'pr-3' : 'pr-10',
        )}
      />
      {!mobile ? (
        <kbd
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded-sm border border-line bg-surface px-1.5 text-micro text-subtle peer-focus:hidden"
        >
          /
        </kbd>
      ) : null}
    </div>
  )

  return (
    <>
      <form role="search" onSubmit={submit} className="hidden w-full max-w-[640px] md:block">
        {field(inputRef, false)}
      </form>
      <IconButton
        aria-label={placeholder}
        icon={<Search />}
        size={40}
        className="md:hidden"
        onClick={() => setMobileOpen(true)}
      />
      {mobileOpen ? (
        <form
          role="search"
          onSubmit={submit}
          className="absolute inset-0 z-10 flex items-center gap-2 bg-surface px-2 md:hidden"
        >
          <IconButton aria-label={t('Close search')} icon={<ArrowLeft />} size={40} onClick={() => setMobileOpen(false)} />
          {field(mobileRef, true)}
        </form>
      ) : null}
    </>
  )
}
